import {
	getMomentTz,
	MESApiService,
	type spParameter,
} from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { useCallback, useEffect, useRef, useState } from "react";

const moment = getMomentTz();

export interface LineShiftInfo {
	CurrentShiftId: number;
	CurrentProductionDate: Date;
	CurrentProductionShift: number;
	CurrentShift: string | null;
	CurrentCrew: string | null;
	CurrentStart: Date;
	CurrentEnd: Date;
	PreviousShiftId: number | null;
	NextShiftId: number | null;
	LastShiftId: number | null;
	LastProductionDate: Date | null;
}

export const useLineShiftNavigator = (
	assetId: number,
	onError: (message: string) => void,
) => {
	const [shiftInfo, setShiftInfo] = useState<LineShiftInfo | null>(null);
	const [loading, setLoading] = useState(false);
	const request = useRef(0);
	const getShiftDataFromAPI = useCallback(
		async (productionDate: Date | null, shiftId: number, lineId = assetId) => {
			const version = ++request.current;
			setLoading(true);
			try {
				const parameters: spParameter[] = [
					{ name: "AssetId", value: lineId },
					{ name: "ShiftId", value: shiftId || null },
					{
						name: "ProductionDate",
						value: productionDate
							? moment.utc(productionDate).format("YYYY-MM-DD")
							: null,
					},
				];
				const response = await new MESApiService().callV2(
					"[FAB].[GetLineShiftByParameters]",
					parameters,
				);
				if (!response.ok)
					throw new Error(response.message || "Unable to load shifts");
				const row = get(response, "data.tables[0].rows[0]");
				if (!row)
					throw new Error("No shifts are available for this line and date");
				if (version !== request.current) return;
				setShiftInfo({
					...row,
					CurrentProductionDate: moment.utc(row.CurrentProductionDate).toDate(),
					CurrentStart: moment.utc(row.CurrentStart).toDate(),
					CurrentEnd: moment.utc(row.CurrentEnd).toDate(),
					LastProductionDate: row.LastProductionDate
						? moment.utc(row.LastProductionDate).toDate()
						: null,
				});
			} catch (error) {
				if (version === request.current)
					onError(
						error instanceof Error ? error.message : "Unable to load shifts",
					);
			} finally {
				if (version === request.current) setLoading(false);
			}
		},
		[assetId, onError],
	);

	useEffect(() => {
		if (!shiftInfo) return;
		const timer = setInterval(() => {
			void getShiftDataFromAPI(
				shiftInfo.CurrentProductionDate,
				shiftInfo.CurrentShiftId,
			);
		}, 60_000);
		return () => clearInterval(timer);
	}, [getShiftDataFromAPI, shiftInfo]);
	useEffect(
		() => () => {
			request.current += 1;
		},
		[],
	);

	return { shiftInfo, loading, getShiftDataFromAPI };
};
